import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Cpu, Database, Layers, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatErr } from "@/lib/api";

const REFRESH_MS = 10000;

const uptime = (s) => {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d} h ${h} j` : h ? `${h} j ${m} mnt` : `${m} mnt`;
};

const Stat = ({ icon: Icon, label, value, testId, tone = "text-zinc-900" }) => (
  <div className="rounded-2xl bg-white border border-zinc-200 p-5">
    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{label}</p>
    <p className={`mt-2 text-2xl font-bold font-mono-x ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

const MemoryCard = ({ mem }) => (
  <div className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="health-memory">
    <div className="flex items-center justify-between mb-3">
      <p className="font-semibold">Penggunaan memori</p>
      <p className="font-mono-x text-sm" data-testid="health-memory-text">{mem.used_mb} MB / {mem.limit_mb ?? "?"} MB{mem.percent != null ? ` (${mem.percent}%)` : ""}</p>
    </div>
    {mem.percent != null && <Progress value={mem.percent} className={mem.percent >= 85 ? "[&>div]:bg-rose-600" : mem.percent >= 70 ? "[&>div]:bg-amber-500" : ""} />}
    <ul className="mt-4 text-sm divide-y divide-zinc-100">
      {mem.processes.map((p) => (
        <li key={p.pid} className="flex justify-between py-1.5" data-testid={`health-proc-${p.pid}`}><span className="text-zinc-600">{p.name} <span className="text-zinc-400">#{p.pid}</span></span><span className="font-mono-x">{p.rss_mb} MB</span></li>
      ))}
    </ul>
  </div>
);

const QueueCard = ({ q, failed }) => (
  <div className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="health-queue">
    <p className="font-semibold mb-3">Antrean proses foto</p>
    <div className="grid grid-cols-3 gap-3 text-center mb-4">
      {[["Menunggu", q.queued, "text-blue-700", "queued"], ["Diproses", q.working, "text-amber-700", "working"], ["Gagal", q.failed, "text-rose-700", "failed"]].map(([l, v, c, k]) => (
        <div key={k} className="rounded-xl bg-zinc-50 py-3"><p className={`text-xl font-bold font-mono-x ${c}`} data-testid={`health-queue-${k}`}>{v}</p><p className="text-xs text-zinc-500">{l}</p></div>
      ))}
    </div>
    {q.events.length > 0 ? (
      <ul className="text-sm divide-y divide-zinc-100" data-testid="health-queue-events">
        {q.events.map((e) => <li key={e.event_id} className="flex justify-between py-1.5"><span className="truncate pr-3">{e.event_title}</span><span className="font-mono-x text-xs text-zinc-500 whitespace-nowrap">{e.queued} antre · {e.working} proses · {e.failed} gagal</span></li>)}
      </ul>
    ) : <p className="text-sm text-zinc-500">Tidak ada antrean.</p>}
    {failed.length > 0 && (
      <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 p-3" data-testid="health-failed-list">
        <p className="text-sm font-semibold text-rose-800 mb-1">Foto gagal terbaru</p>
        <ul className="text-xs text-rose-900 space-y-0.5 max-h-32 overflow-y-auto">{failed.map((f) => <li key={f.id} className="truncate">{f.event_title} / {f.filename}: {f.error || "-"}</li>)}</ul>
      </div>
    )}
  </div>
);

export const AdminHealth = () => {
  const [h, setH] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get("/admin/health").then((r) => { setH(r.data); setErr(null); }).catch((e) => setErr(formatErr(e))), []);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!h) return <p className="text-zinc-500" data-testid="health-loading">{err || "Memuat..."}</p>;
  const ok = h.status === "ok";
  return (
    <section className="space-y-6" data-testid="admin-health">
      <div className={`rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="health-status">
        <div className="flex items-start gap-2">
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />}
          <div>
            <p className="font-semibold" data-testid="health-status-text">{ok ? "Semua sistem normal" : "Perlu perhatian"}</p>
            {h.warnings.map((w) => <p key={w} className="text-sm text-amber-800">{w}</p>)}
            {err && <p className="text-sm text-rose-700">Gagal memperbarui: {err}</p>}
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-lg bg-white" onClick={load} data-testid="health-refresh-button"><RefreshCw className="h-4 w-4 mr-1" />Perbarui</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Activity} label="Aktif sejak" value={uptime(h.uptime_seconds)} testId="health-uptime" />
        <Stat icon={Database} label="Ping database" value={`${h.db_ping_ms} ms`} testId="health-db-ping" />
        <Stat icon={Cpu} label="Worker wajah" value={`${h.queue_tasks_alive}/${h.face_workers}`} testId="health-workers" tone={h.queue_tasks_alive < h.face_workers ? "text-rose-700" : "text-zinc-900"} />
        <Stat icon={Layers} label="Indeks wajah" value={h.face_index_loaded ? h.face_index_size.toLocaleString("id-ID") : "Belum dimuat"} testId="health-face-index" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <MemoryCard mem={h.memory} />
        <QueueCard q={h.queue} failed={h.failed_items} />
      </div>
      <p className="text-xs text-zinc-400">Diperbarui otomatis tiap {REFRESH_MS / 1000} detik · {h.totals.photos.toLocaleString("id-ID")} foto · {h.totals.users} pengguna · {h.totals.orders_paid} pesanan lunas</p>
    </section>
  );
};
